import { useContext, useEffect, useState } from "react";
import {
  getUserStatistics,
  getUserStatisticsWithDate,
} from "../api/UserStatistics.api";
import { ActiveUserContext } from "../utils/contexts";
import { UserStatistics } from "../models/UserStatistics.model";
import styled from "styled-components";
import { getDateString } from "../models/Utils";
import Title from "./Title";
import { BackButton, Tab } from "./Common";

interface StatisticsPageProps {
  navigate: (t: Tab) => void;
}

const LoadingContainer = styled.div`
  margin: 0px auto 10px auto;
  max-width: 400px;
  padding: 0px 10px 10px 10px;
`;

const PageTitle = styled.div`
  margin-bottom: 30px;
`;

const LoadingMessage = styled.div`
  height: 207.5px;
  line-height: 207.5px;
`;

const StatsContainer = styled.div`
  background-color: ${(props) => props.theme.SECONDARY};
  padding-bottom: 20px;
`;

const StatsHeader = styled.div`
  display: flex;
`;

const StatsOption = styled.div<{ selected: boolean }>`
  background-color: ${({ selected, theme }) =>
    selected ? theme.SECONDARY : theme.DISABLED};
  padding: 8px 16px;
  flex: 1;

  cursor: ${(props) => (props.selected ? "default" : "pointer")};

  &:hover {
    background-color: ${(props) =>
      props.selected ? props.theme.SECONDARY : props.theme.HIGHLIGHT};
  }
`;

const StatRow = styled.div`
  display: flex;
  padding: 20px 20px 0px 20px;
`;

const StatTitle = styled.div`
  text-align: left;
  flex: 1;
`;

const StatValue = styled.div`
  text-align: right;
  flex: 1;
  color: ${(props) => props.theme.PRIMARY};
`;

const StatisticsPage = ({ navigate }: StatisticsPageProps) => {
  const activeUserId = useContext(ActiveUserContext);
  const [stats, setStats] = useState<UserStatistics | null>(null);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<
    "week" | "month" | "total"
  >("total");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    setStats(null);
    let startDate;
    let dateString;
    if (selectedTimePeriod === "week") {
      startDate = new Date();
      startDate.setTime(startDate.getTime() - 6 * 24 * 60 * 60 * 1000);
      dateString = getDateString(startDate);
      getUserStatisticsWithDate(activeUserId, dateString).then((res) => {
        setStats(res);
      });
    } else if (selectedTimePeriod === "month") {
      let startDate = new Date();
      startDate.setTime(startDate.getTime() - 29 * 24 * 60 * 60 * 1000);
      let dateString = getDateString(startDate);
      getUserStatisticsWithDate(activeUserId, dateString).then((res) => {
        setStats(res);
      });
    } else {
      getUserStatistics(activeUserId).then((res) => {
        setStats(res);
      });
    }
  }, [selectedTimePeriod, activeUserId]);

  return (
    <>
      <Title />
      <PageTitle>User Statistics</PageTitle>
      <LoadingContainer>
        <StatsContainer>
          <StatsHeader>
            <StatsOption
              selected={selectedTimePeriod === "total"}
              onClick={() => setSelectedTimePeriod("total")}
            >
              Lifetime
            </StatsOption>
            <StatsOption
              selected={selectedTimePeriod === "week"}
              onClick={() => setSelectedTimePeriod("week")}
            >
              7 Days
            </StatsOption>
            <StatsOption
              selected={selectedTimePeriod === "month"}
              onClick={() => setSelectedTimePeriod("month")}
            >
              30 Days
            </StatsOption>
          </StatsHeader>
          {stats ? (
            <>
              <StatRow>
                <StatTitle>Total Games:</StatTitle>
                <StatValue>{stats.totalGames}</StatValue>
              </StatRow>
              <StatRow>
                <StatTitle>Total Words:</StatTitle>
                <StatValue>{stats.totalWords}</StatValue>
              </StatRow>
              <StatRow>
                <StatTitle>Total Points:</StatTitle>
                <StatValue>{stats.totalPoints}</StatValue>
              </StatRow>
              <StatRow>
                <StatTitle>Longest Streak:</StatTitle>
                <StatValue>{stats.longestStreak}</StatValue>
              </StatRow>
              <StatRow>
                <StatTitle>Longest Word:</StatTitle>
                <StatValue>
                  {stats.longestWords.join(" ").toUpperCase()}
                </StatValue>
              </StatRow>
            </>
          ) : (
            <LoadingMessage>Loading...</LoadingMessage>
          )}
        </StatsContainer>
      </LoadingContainer>
      <BackButton onClick={() => navigate("homepage")}>Back</BackButton>
    </>
  );
};

export default StatisticsPage;
